/* src/components/game/PositionGrid.js */
import React, { useState, useEffect, useRef } from 'react';

// --- Simple pulse animation CSS ---
// Defines the keyframes and the class to apply the animation.
// animation: pulse 0.2s ease-in-out; ensures it runs once for 200ms.
const pulseAnimationStyle = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  .pulse-animation {
    animation: pulse 0.2s ease-in-out;
  }
`;

/**
 * PositionGrid Component: Displays the 3x3 grid for visual stimuli.
 * Handles stimulus display, hiding, feedback, novice help numbers,
 * and the pulse animation for the active stimulus in live, replay, and tutorial modes.
 */
const PositionGrid = (props) => {
  // Determine the current operating mode based on props
  const isReplayMode = props.isReplayMode ?? (props.activePosition !== undefined && !props.isTutorialMode);
  const isTutorialMode = props.isTutorialMode ?? false;
  const isLiveMode = !isReplayMode && !isTutorialMode;

  // --- Live Gameplay State Management ---
  // This state is ONLY used for live gameplay to manage the currently
  // displayed stimulus and its pulse state, controlled by timers.
  const [gridDisplayState, setGridDisplayState] = useState(() => {
    const initialState = isLiveMode ? { currentPosition: null, pulsePosition: null } : null;
    // console.log('[PositionGrid] Initializing live mode state:', initialState); // Optional debug log
    return initialState;
  });
  const hideStimulusTimerRef = useRef(null); // Timer for hiding stimulus based on difficulty
  const clearPulseTimerRef = useRef(null); // Timer for clearing the pulse state in live mode

  // --- Effect: Handles Stimulus Display, Hiding, and Pulse State (Live Mode Only) ---
  // This effect runs only during live gameplay to control the stimulus presentation cycle.
  useEffect(() => {
    // console.log(`[PositionGrid Live Effect Run] Trial: ${props.currentTrial}, GameState: ${props.gameState}, isLiveMode: ${isLiveMode}`); // Optional debug log

    // Destructure props needed for live mode logic
    const { currentTrial, sequence, difficulty, logReplayEvent, stimulusDisplayDuration, gameState } = props;

    // Guard clause: Only run this effect logic if in live mode and necessary props are available
    if (!isLiveMode || !sequence || typeof currentTrial !== 'number' || !logReplayEvent) {
      // console.log('[PositionGrid Live Effect] Guard clause triggered or not in live mode, resetting state.'); // Optional debug log
      // Ensure state is reset if we transition out of live mode or props become invalid
      setGridDisplayState(prev => {
        if (prev?.currentPosition !== null || prev?.pulsePosition !== null) {
           // console.log('[PositionGrid Live Effect] Guard: Resetting state from:', prev); // Optional debug log
           return { currentPosition: null, pulsePosition: null };
        }
        return prev;
      });
      // Clear any running timers
      if (hideStimulusTimerRef.current) { clearTimeout(hideStimulusTimerRef.current); hideStimulusTimerRef.current = null; }
      if (clearPulseTimerRef.current) { clearTimeout(clearPulseTimerRef.current); clearPulseTimerRef.current = null; }
      return; // Exit effect
    }

    // Determine the target position for the current trial in live mode
    let targetPosition = null;
    if (gameState === 'playing' && currentTrial >= 0 && currentTrial < sequence.length) {
        targetPosition = sequence[currentTrial]?.position;
        // console.log(`[PositionGrid Live Effect] Trial ${currentTrial}: Target position = ${targetPosition}`); // Optional debug log
    } else {
        // console.log(`[PositionGrid Live Effect] Trial ${currentTrial}: Not playing or invalid trial, target = null`); // Optional debug log
    }

    // Determine if the stimulus should pulse (always pulses if there's a target position in live mode)
    const shouldPulse = targetPosition !== null;

    // --- Clear Previous Timers ---
    // Crucial to prevent old timers from interfering with the new state
    if (hideStimulusTimerRef.current) {
        // console.log('[PositionGrid Live Effect] Clearing previous hide timer.'); // Optional debug log
        clearTimeout(hideStimulusTimerRef.current);
        hideStimulusTimerRef.current = null;
    }
    if (clearPulseTimerRef.current) {
        // console.log('[PositionGrid Live Effect] Clearing previous pulse timer.'); // Optional debug log
        clearTimeout(clearPulseTimerRef.current);
        clearPulseTimerRef.current = null;
    }

    // --- Update Visual State for Live Mode ---
    // Set the current position and the pulse position simultaneously
    const newState = {
        currentPosition: targetPosition,
        pulsePosition: shouldPulse ? targetPosition : null
    };
    // console.log('[PositionGrid Live Effect] Calling setGridDisplayState with:', newState); // Optional debug log
    setGridDisplayState(newState);

    // --- Set New Timers (Hide Stimulus and Clear Pulse) ---
    if (targetPosition !== null && gameState === 'playing') {
        // Log the stimulus appearance for replay purposes
        logReplayEvent({ type: 'visualStimulusOn', trialIndex: currentTrial, position: targetPosition });

        // 1. Timer to HIDE the stimulus (based on difficulty)
        if (difficulty > 0.20 && stimulusDisplayDuration !== null && stimulusDisplayDuration > 0) {
            const positionToHide = targetPosition; // Capture current position for the timer closure
            const trialHiding = currentTrial; // Capture current trial for the timer closure
            // console.log(`[PositionGrid Live Effect] Setting HIDE timer (${stimulusDisplayDuration}ms) for position ${positionToHide}, trial ${trialHiding}`); // Optional debug log
            hideStimulusTimerRef.current = setTimeout(() => {
                // console.log(`[PositionGrid Hide Timer Callback] Fired for position ${positionToHide}, trial ${trialHiding}`); // Optional debug log
                logReplayEvent({ type: 'visualStimulusOff', trialIndex: trialHiding }); // Log stimulus removal
                setGridDisplayState(prev => {
                    // console.log(`[PositionGrid Hide Timer Callback] Current state before hide:`, prev); // Optional debug log
                    // Only hide if the current position hasn't changed in the meantime
                    if (prev?.currentPosition === positionToHide) {
                        // console.log(`[PositionGrid Hide Timer Callback] Hiding position ${positionToHide}.`); // Optional debug log
                        // Set currentPosition to null, leave pulsePosition (it has its own timer)
                        return { ...prev, currentPosition: null };
                    }
                    // console.log(`[PositionGrid Hide Timer Callback] Skipped hiding, current position (${prev?.currentPosition}) doesn't match ${positionToHide}.`); // Optional debug log
                    return prev; // Otherwise, keep state as is
                });
                hideStimulusTimerRef.current = null; // Mark timer as finished
            }, stimulusDisplayDuration);
        } else {
             // console.log(`[PositionGrid Live Effect] Not setting HIDE timer (difficulty <= 0.20 or invalid duration)`); // Optional debug log
        }

        // 2. Timer to CLEAR the pulse state (only if pulse was triggered)
        if (shouldPulse) {
            const positionToClearPulse = targetPosition; // Capture position for timer closure
            // console.log(`[PositionGrid Live Effect] Setting PULSE CLEAR timer (200ms) for position ${positionToClearPulse}, trial ${currentTrial}`); // Optional debug log
            clearPulseTimerRef.current = setTimeout(() => {
                 // console.log(`[PositionGrid Pulse Clear Timer Callback] Fired for position ${positionToClearPulse}, trial ${currentTrial}`); // Optional debug log
                setGridDisplayState(prev => {
                    // console.log(`[PositionGrid Pulse Clear Timer Callback] Current state before clear:`, prev); // Optional debug log
                    // Only clear the pulse if this position is still the one marked for pulsing
                    if (prev?.pulsePosition === positionToClearPulse) {
                         // console.log(`[PositionGrid Pulse Clear Timer Callback] Clearing pulse for ${positionToClearPulse}.`); // Optional debug log
                        return { ...prev, pulsePosition: null };
                    }
                     // console.log(`[PositionGrid Pulse Clear Timer Callback] Skipped clearing pulse for ${positionToClearPulse}, current pulse is ${prev?.pulsePosition}.`); // Optional debug log
                    return prev; // Otherwise, keep state as is
                });
                clearPulseTimerRef.current = null; // Mark timer as finished
            }, 200); // Duration matches the pulse animation (0.2s)
        }
    } else {
        // console.log(`[PositionGrid Live Effect] Not setting timers (targetPosition is null or gameState is not 'playing')`); // Optional debug log
    }

    // --- Effect Cleanup ---
    // Clears timers when the component unmounts or dependencies change,
    // preventing memory leaks and incorrect behavior.
    return () => {
      // console.log(`[PositionGrid Live Effect Cleanup] For Trial: ${currentTrial}`); // Optional debug log
      if (hideStimulusTimerRef.current) {
        // console.log('[PositionGrid Live Effect Cleanup] Clearing hide timer.'); // Optional debug log
        clearTimeout(hideStimulusTimerRef.current);
        hideStimulusTimerRef.current = null;
      }
       if (clearPulseTimerRef.current) {
        // console.log('[PositionGrid Live Effect Cleanup] Clearing pulse timer.'); // Optional debug log
        clearTimeout(clearPulseTimerRef.current);
        clearPulseTimerRef.current = null;
      }
    };
  // Dependencies for the live mode effect
  }, [
      isLiveMode, props.currentTrial, props.sequence, props.gameState,
      props.difficulty, props.stimulusDisplayDuration, props.logReplayEvent
  ]);


  // --- Calculate Missed Position Highlight (Live Mode Specific) ---
  // Determines which square to highlight red if instant feedback is on and a match was missed.
  const missedPositionIndexLive = (
      isLiveMode && props.instantFeedbackIsEnabled && props.missedMatchTrial !== null &&
      props.missedMatchTrial >= 0 && props.sequence?.[props.missedMatchTrial]
    ) ? props.sequence[props.missedMatchTrial].position : null;


  // --- Render the Grid ---
  // console.log('[PositionGrid Render] Live mode state:', gridDisplayState); // Optional debug log
  return (
    <>
      {/* Inject the pulse animation keyframes into the document */}
      <style>{pulseAnimationStyle}</style>
      {/* Grid container */}
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2 w-full aspect-square max-w-[280px] mx-auto sm:max-w-[320px] lg:w-72 lg:h-72 lg:max-w-none relative">
        {/* Map over 9 positions to create the cells */}
        {Array(9).fill(null).map((_, positionIndex) => {
          // --- START OF MAP CALLBACK for cell rendering ---
          // Destructure props needed for rendering logic within the loop
          const {
              showNoviceHelp, nBack: nBackProp, sequence: sequenceProp,
              currentTrial: currentTrialProp, visualFeedback, visualResponseTrials = [],
              instantFeedbackIsEnabled,
              // Props specific to replay/tutorial mode:
              activePosition, // The position to highlight in non-live modes
              feedbackState, // Feedback state ('correct', 'incorrect', 'missed') in non-live modes
              missedHighlightPositionIndex // Index to highlight red for misses in non-live modes
          } = props;

          // Initialize variables for the current cell's appearance
          let cellStyle = 'bg-white border-gray-300'; // Default style: white background, gray border
          let dotStyle = 'bg-indigo-500'; // Default dot color: indigo
          let dotOpacity = 0; // Default dot visibility: hidden
          let numbersToRender = []; // Array to hold novice helper numbers for this cell
          let numberColorClass = 'text-gray-600'; // Default color for novice numbers
          let isPulsing = false; // Initialize pulsing state for this cell to false

          // --- Determine Cell Style and Pulsing Based on Game Mode ---
          if (isReplayMode) {
            // Replay Mode Logic: Style based on props like activePosition and feedbackState
            const isMissedHighlight = positionIndex === missedHighlightPositionIndex; // Is this the cell for a missed match?
            const isActive = positionIndex === activePosition; // Is this the cell for the current stimulus?

            // --- REPLAY MODE PULSE LOGIC ---
            // Pulse the dot if this cell corresponds to the active stimulus position
            isPulsing = isActive;
            // --- END REPLAY MODE PULSE LOGIC ---

            if (isMissedHighlight) {
                // Style for a missed match highlight
                cellStyle = 'bg-red-50 border-red-300'; // Red background/border
                dotStyle = 'bg-red-500'; // Red dot
                dotOpacity = 1; // Make dot visible
            } else if (isActive) {
                // Style for the active stimulus position
                dotOpacity = 1; // Make dot visible
                // Adjust colors based on feedback state provided via props
                if (feedbackState === 'correct') { cellStyle = 'bg-green-50 border-green-300'; dotStyle = 'bg-green-500'; }
                else if (feedbackState === 'incorrect' || feedbackState === 'missed') { cellStyle = 'bg-red-50 border-red-300'; dotStyle = 'bg-red-500'; }
                else { cellStyle = 'bg-indigo-50 border-indigo-300'; } // Default active style (indigo)
            }

            // Novice Help Logic (Replay Mode)
            // Check past trials if helpers are enabled and this cell isn't the active one
            if (showNoviceHelp && !isActive && !isMissedHighlight && sequenceProp && typeof currentTrialProp === 'number' && currentTrialProp >= 0) {
                const potentialNumbers = [];
                // Look back up to N trials
                for (let ago = 1; ago <= nBackProp; ago++) {
                    const pastTrialIndex = currentTrialProp - ago;
                    // If a past trial matches this position, add the 'N' value (how many steps ago)
                    if (pastTrialIndex >= 0 && sequenceProp[pastTrialIndex]?.position === positionIndex) {
                        potentialNumbers.push(ago); // Store N value (1, 2, ... N)
                    }
                }
                // Remove duplicates and sort the numbers
                numbersToRender = [...new Set(potentialNumbers)].sort((a, b) => a - b);
                // If numbers found, style the cell for number display and hide the dot
                if (numbersToRender.length > 0) { cellStyle = 'bg-gray-50 border-gray-300'; dotOpacity = 0; }
            }

          } else if (isTutorialMode) {
            // Tutorial Mode Logic: Similar to replay, based on activePosition and feedbackState props
            const isActive = positionIndex === activePosition;

            // --- TUTORIAL MODE PULSE LOGIC ---
            isPulsing = isActive;
            // --- END TUTORIAL MODE PULSE LOGIC ---

            if (isActive) {
                dotOpacity = 1; // Show dot
                // Set colors based on feedback
                if (feedbackState === 'correct') { cellStyle = 'bg-green-50 border-green-300'; dotStyle = 'bg-green-500'; }
                else if (feedbackState === 'incorrect') { cellStyle = 'bg-red-50 border-red-300'; dotStyle = 'bg-red-500'; }
                else { cellStyle = 'bg-indigo-50 border-indigo-300'; } // Default active style
            }

            // Novice Help Logic (Tutorial Mode)
            if (showNoviceHelp && !isActive && sequenceProp && typeof currentTrialProp === 'number' && currentTrialProp >= 0) {
                const potentialNumbers = [];
                for (let ago = 1; ago <= nBackProp; ago++) {
                    const pastTrialIndex = currentTrialProp - ago;
                    if (pastTrialIndex >= 0 && sequenceProp[pastTrialIndex]?.position === positionIndex) {
                        potentialNumbers.push(ago);
                    }
                }
                numbersToRender = [...new Set(potentialNumbers)].sort((a, b) => a - b);
                if (numbersToRender.length > 0) { cellStyle = 'bg-gray-50 border-gray-300'; dotOpacity = 0; }
            }

          } else { // Live Mode Logic
            // Style based on internal gridDisplayState and live feedback props
            const isMissedPosition = positionIndex === missedPositionIndexLive; // Missed match highlight?
            const isCurrentPosition = gridDisplayState?.currentPosition === positionIndex; // Current stimulus?

            // --- LIVE MODE PULSE LOGIC ---
            // Use the internal state managed by timers
            isPulsing = gridDisplayState?.pulsePosition === positionIndex;
            // --- END LIVE MODE PULSE LOGIC ---

            // Novice Help Logic (Live Mode)
            if (showNoviceHelp && !isCurrentPosition && sequenceProp && typeof currentTrialProp === 'number' && currentTrialProp >= 0) {
                const potentialNumbers = [];
                for (let ago = 1; ago <= nBackProp; ago++) {
                    const pastTrialIndex = currentTrialProp - ago;
                    if (pastTrialIndex >= 0 && sequenceProp[pastTrialIndex]?.position === positionIndex) {
                        potentialNumbers.push(ago);
                    }
                }
                numbersToRender = [...new Set(potentialNumbers)].sort((a, b) => a - b);
                // Style for number display only if we aren't showing the main stimulus or feedback
                if (numbersToRender.length > 0 && !isMissedPosition) { cellStyle = 'bg-gray-50 border-gray-300'; }
            }

            // Determine final style based on priority: Missed > Current Stimulus > Novice Numbers
            if (isMissedPosition) {
                // Highest priority: Missed match feedback
                cellStyle = 'bg-red-50 border-red-300'; // Red background/border
                dotStyle = 'bg-red-500'; // Red dot
                dotOpacity = 1; // Show dot
                numbersToRender = []; // Hide numbers if showing feedback
            } else if (isCurrentPosition) {
                // Next priority: Show the current stimulus
                // Use subtle background unless feedback is active
                cellStyle = 'bg-gray-50 border-gray-300';
                numbersToRender = []; // Hide numbers when showing stimulus
                dotOpacity = 1; // Show dot
                // Check for instant feedback on response trials
                const wasResponseTrial = typeof currentTrialProp === 'number' ? visualResponseTrials.includes(currentTrialProp) : false;
                if (instantFeedbackIsEnabled && visualFeedback && wasResponseTrial) {
                    // Apply feedback colors if applicable
                    if (visualFeedback === 'correct') { cellStyle = 'bg-green-50 border-green-300'; dotStyle = 'bg-green-500'; }
                    else if (visualFeedback === 'incorrect') { cellStyle = 'bg-red-50 border-red-300'; dotStyle = 'bg-red-500'; }
                }
            } else if (numbersToRender.length > 0) {
                // Lowest priority: Show novice numbers if nothing else is active
                dotOpacity = 0; // Hide dot
            } else {
                // Default inactive cell state
                dotOpacity = 0; // Hide dot
                numbersToRender = []; // No numbers
            }
          }

          // Determine final visibility flags for rendering
          const showHelperNumbers = numbersToRender.length > 0;
          const showDot = dotOpacity >= 0.1; // Use a threshold to handle potential floating point issues

          // --- Render the actual div for this cell ---
          return (
            <div
              key={`grid-cell-${positionIndex}`}
              // Apply base classes, dynamic cellStyle, and transitions
              className={`relative flex items-center justify-center border rounded-md aspect-square shadow-sm ${cellStyle} transition-colors duration-100`}
            >
              {/* Render the dot if it should be visible */}
              {showDot && (
                <div
                  // Apply base dot styles, dynamic dotStyle, and the pulse animation class if 'isPulsing' is true
                  className={`w-[50%] h-[50%] rounded-full absolute ${dotStyle} ${isPulsing ? 'pulse-animation' : ''}`}
                  style={{ opacity: dotOpacity }} // Control visibility via opacity
                  aria-hidden="true" // Hide from accessibility tree as it's purely visual
                />
              )}
              {/* Render novice helper numbers if they exist */}
              {showHelperNumbers && (
                 <div
                   className="absolute inset-0 flex items-center justify-center text-center pointer-events-none"
                   aria-hidden="true" // Hide from accessibility tree
                 >
                    {/* Map over the numbers to render */}
                    {numbersToRender.map((number, index) => (
                        <span
                          key={index}
                          // Apply dynamic number color, font styles, and spacing
                          className={`${numberColorClass} font-bold text-base sm:text-lg select-none px-0.5`}
                        >
                           {/* Display 'N' if the number matches the nBack level, otherwise display the number */}
                           {number === nBackProp ? 'N' : number}
                        </span>
                    ))}
                 </div>
              )}
            </div>
          );
          // --- END OF MAP CALLBACK ---
        })}
      </div>
    </>
  );
};

export default PositionGrid;